import { memo } from '@tanstack/react-table';

import { Table, Row, RowModel, RowData } from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2ColumnType } from '../ColumnTypes/ColumnTypes.js';

/**
 * Sorted row model copied from the tanstack/react-table and adapted
 * for handling null and undefined values and control over custom sorting functionality.
 */
export function getSortedRowModel<TData extends RowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table: Table<TData>) =>
    memo(
      () => [table.getState().sorting, table.getPreSortedRowModel()],
      (sorting, rowModel) => {
        if (!rowModel.rows.length || !sorting?.length) {
          return rowModel;
        }

        const sortingState = table.getState().sorting;

        const sortedFlatRows: Row<TData>[] = [];

        // Filter out sortings that correspond to non existing columns
        const availableSorting = sortingState.filter((sort) =>
          table.getColumn(sort.id)?.getCanSort(),
        );

        const columnInfoById: Record<
          string,
          {
            sortUndefined?: false | -1 | 1 | 'first' | 'last';
            invertSorting?: boolean;
            columnType?: DataTableV2ColumnType;
            customSortingFn: (valueA: unknown, valueB: unknown) => number;
          }
        > = {};

        availableSorting.forEach((sortEntry) => {
          const column = table.getColumn(sortEntry.id);
          if (!column) {
            return;
          }

          columnInfoById[sortEntry.id] = {
            sortUndefined: column.columnDef.sortUndefined,
            invertSorting: column.columnDef.invertSorting,
            columnType: column.columnDef.columnType,
            customSortingFn: column.getCustomSortingFn(),
          };
        });

        const sortData = (rows: Row<TData>[]) => {
          // This will also perform a stable sorting using the row index
          // if needed.
          const sortedData = rows.map((row) => ({ ...row }));

          sortedData.sort((rowA, rowB) => {
            for (let i = 0; i < availableSorting.length; i += 1) {
              const sortEntry = availableSorting[i];
              const columnInfo = columnInfoById[sortEntry.id];
              const isDesc = sortEntry?.desc ?? false;

              let sortInt = 0;

              // All sorting ints should always return in ascending order
              if (columnInfo.sortUndefined) {
                const aValue = rowA.getSortingValue(sortEntry.id);
                const bValue = rowB.getSortingValue(sortEntry.id);

                const aUndefined =
                  aValue === undefined ||
                  aValue === null ||
                  (columnInfo.columnType !== undefined && Number.isNaN(aValue));
                const bUndefined =
                  bValue === undefined ||
                  bValue === null ||
                  (columnInfo.columnType !== undefined && Number.isNaN(bValue));

                if (aUndefined || bUndefined) {
                  if (aUndefined && bUndefined) {
                    sortInt = 0;
                  }
                  // sort null and undefined always at the bottom
                  // sort NaN at the bottom if `columnType` is defined otherwise NaN is compared alphanumerically by default
                  const sorting =
                    (columnInfo.invertSorting && isDesc) ||
                    (!columnInfo.invertSorting && !isDesc);

                  if (aUndefined) {
                    sortInt = sorting ? 1 : -1;
                  } else {
                    sortInt = sorting ? -1 : 1;
                  }
                }
              }

              if (sortInt === 0) {
                sortInt = columnInfo.customSortingFn(
                  rowA.getSortingValue(sortEntry.id),
                  rowB.getSortingValue(sortEntry.id),
                );
              }

              // If sorting is non-zero, take care of desc and inversion
              if (sortInt !== 0) {
                if (isDesc) {
                  sortInt *= -1;
                }

                if (columnInfo.invertSorting) {
                  sortInt *= -1;
                }

                return sortInt;
              }
            }

            return rowA.index - rowB.index;
          });

          // If there are sub-rows, sort them
          sortedData.forEach((row) => {
            sortedFlatRows.push(row);
            if (row.subRows?.length) {
              row.subRows = sortData(row.subRows);
            }
          });

          return sortedData;
        };

        return {
          rows: sortData(rowModel.rows as unknown as Row<TData>[]),
          flatRows: sortedFlatRows,
          rowsById: rowModel.rowsById,
        };
      },
      getMemoOptions(table.options, 'debugTable', 'getSortedRowModel', () =>
        table._autoResetPageIndex(),
      ),
    );
}
